import { useState } from "react";
import { useCopyToClipboard } from "react-use";
import Check from "@mui/icons-material/Check";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { FILE_BROWSER_API } from "~/lib/fileBrowserApi";
import { apiMutate } from "~/utils/apiClient";

interface PresignedUrlItem {
  key: string;
  url: string;
  expires_at: string;
}

interface ShareLinksManifestProps {
  fileKey: string;
  open: boolean;
  onClose: () => void;
}

const EXAMPLE_SCRIPT = `#!/bin/bash
# Run from the same directory as share-links.json. Requires: curl, jq.
count=$(jq length share-links.json)
for i in $(seq 0 $((count - 1))); do
  url=$(jq -r ".[$i].url" share-links.json)
  key=$(jq -r ".[$i].key" share-links.json)
  filename=$(basename "$key")
  echo "Downloading $filename..."
  curl -fLo "$filename" "$url"
done
echo "Done."`;

const ShareLinksManifest = ({ fileKey, open, onClose }: ShareLinksManifestProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(false);
  const [scriptCopied, setScriptCopied] = useState(false);
  const [, copy] = useCopyToClipboard();

  const handleCopyScript = () => {
    copy(EXAMPLE_SCRIPT);
    setScriptCopied(true);
    setTimeout(() => setScriptCopied(false), 2000);
  };

  const handleClose = () => {
    setError(false);
    onClose();
  };

  const handleDownload = async () => {
    if (isGenerating) {
      return;
    }
    setIsGenerating(true);
    setError(false);
    try {
      const items = await apiMutate<PresignedUrlItem[]>(
        `${FILE_BROWSER_API}/download-urls`,
        "POST",
        { keys: [fileKey] }
      );
      const blob = new Blob([JSON.stringify(items, null, 2)], { type: "application/json" });
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.style.display = "none";
      anchor.download = "share-links.json";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
      onClose();
    } catch {
      setError(true);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>Generate share links</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 14 }}>
        {error && <Alert severity="error">Unable to generate share links</Alert>}
        <Typography variant="body2" color="text.secondary">
          You are generating a JSON file with pre-signed URLs that should only be shared with a
          trusted collaborator. Run the script below within one hour in your preferred destination
          to download the files.
        </Typography>
        <Box>
          <Typography variant="caption" fontWeight={500}>
            Example download script
          </Typography>
          <Box sx={{ position: "relative" }}>
            <Box
              component="pre"
              sx={{
                bgcolor: "grey.100",
                borderRadius: 1,
                p: 1.5,
                pr: 5,
                fontSize: 12,
                fontFamily: "monospace",
                overflowX: "auto",
              }}
            >
              {EXAMPLE_SCRIPT}
            </Box>
            <IconButton
              onClick={handleCopyScript}
              size="small"
              aria-label="Copy script"
              sx={{ position: "absolute", top: 4, right: 4 }}
            >
              {scriptCopied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton variant="contained" loading={isGenerating} onClick={handleDownload}>
          Download share-links.json
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ShareLinksManifest;
